import { DEFAULT_ERROR_CODE, DEFAULT_ERROR_MESSAGE, MARKETPLACE_HEADERS } from "constants/bill";
import { DELAYED_TOAST_CONSTANTS } from "constants/delayedToast";
import {
    CHECKOUT_WEB_APP_URL,
    CROUTON_WEBVIEW_IOS_VERSION,
    PREEBOOK_PREPAID,
    EVENTS,
    GIRF_PAYMENT,
} from "constants/index";
import { Dispatch } from "react";
import { format, safeJsonParse } from "@swiggy-private/js-utils";
import type { RawOrderData } from "@swiggy-private/native-bridge";
import { QrCodeActions } from "@swiggy-private/portal-dineout-context";
import { ToastType, type IQrCodeAction } from "@swiggy-private/portal-dineout-context";
import { isCurrentUserHost } from "@swiggy-private/portal-dineout-ui";
import { getBridgeSdk } from "./bridgeSdk";
import { isIOS } from "./browser";
import { IOrderFailedEvent } from "./paymentTypes";

export const showToast = ({
    dispatch,
    type,
    message,
}: {
    dispatch: Dispatch<IQrCodeAction>;
    type: ToastType;
    message: string;
}): void => {
    dispatch({
        type: QrCodeActions.SHOW_TOAST,
        payload: {
            type,
            message,
        },
    });
};

export const getToastBottomHeightOrderPage = (elementId: string): number => {
    const ctaListContainer = document.getElementById(elementId);
    if (!ctaListContainer) {
        return DELAYED_TOAST_CONSTANTS.TOAST_BOTTOM_MARGIN;
    }
    return ctaListContainer.offsetHeight + DELAYED_TOAST_CONSTANTS.TOAST_BOTTOM_MARGIN;
};

export const getToastBottomHeightMenuPage = (isCartItemAvailable: boolean): number => {
    if (isCartItemAvailable) {
        return DELAYED_TOAST_CONSTANTS.BOTTOM_BAR_HEIGHT_WITH_CART + DELAYED_TOAST_CONSTANTS.TOAST_BOTTOM_MARGIN;
    } else {
        return DELAYED_TOAST_CONSTANTS.BOTTOM_BAR_HEIGHT + DELAYED_TOAST_CONSTANTS.TOAST_BOTTOM_MARGIN;
    }
};

export const getToastWidth = (toastMargin?: number) => {
    if (toastMargin) {
        return `calc(100vw - ${toastMargin}px)`;
    } else {
        return "100vw";
    }
};

export const getToastStyle = (bottomHeight: number, toastWidth?: string, sab = "0px") => ({
    zIndex: DELAYED_TOAST_CONSTANTS.Z_INDEX,
    height: DELAYED_TOAST_CONSTANTS.HEIGHT,
    width: toastWidth ? toastWidth : "calc(100vw - 30px)",
    bottom: `calc(${sab} + ${bottomHeight}px)`,
    marginLeft: DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN,
    position: DELAYED_TOAST_CONSTANTS.POSITION,
});

export const convertKeysToLowerCase = (
    obj?: Record<string, string | number | undefined>,
): Record<string, string | number | undefined> => {
    if (obj == null || Array.isArray(obj) || typeof obj !== "object") {
        return {}; // If not an object, return empty object
    }
    const convertedObj: Record<string, string | number | undefined> = {};
    Object.keys(obj).forEach((key) => {
        const newKey = key.toLowerCase(); // Convert key to lowercase
        const value = obj[key];
        convertedObj[newKey] = value;
    });
    return convertedObj;
};

export const loadScript = (src: string, maxAttempts = 3, currentAttempt = 1): Promise<boolean> => {
    if (typeof window === "undefined") {
        return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            resolve(true);
        };

        script.onerror = () => {
            // Cleanup failed node
            try {
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            } catch {
                // no-op
            }

            if (currentAttempt < maxAttempts) {
                loadScript(src, maxAttempts, currentAttempt + 1).then((succeeded) => resolve(succeeded));
            } else {
                resolve(false);
            }
        };

        document.body.appendChild(script);
    });
};

export const getQueryParamAsString = (query: Record<string, string | string[] | undefined>, key: string): string => {
    const value = query[key];

    if (typeof value === "string") {
        return value;
    }

    if (Array.isArray(value)) {
        return value[0] ?? "";
    }

    return "";
};

export function storeOrderInfoEvent(orderData: RawOrderData, versionCode: string): void {
    const metaDataJson = safeJsonParse<Record<string, string>>(
        orderData?.orders?.[0]?.order_jobs?.[0]?.metadata || "{}",
    );

    if (
        (metaDataJson?.dealCategory === "" || metaDataJson?.type === GIRF_PAYMENT) &&
        metaDataJson?.dealCategory !== EVENTS &&
        metaDataJson?.dealCategory !== PREEBOOK_PREPAID
    ) {
        let orderInfoUrl;
        const redirectURL = `${CHECKOUT_WEB_APP_URL}/dineout/success`;
        if (!isIOS() || Number(versionCode) >= CROUTON_WEBVIEW_IOS_VERSION) {
            const orderId = Number(orderData.order_id);
            if (!isNaN(orderId)) {
                orderInfoUrl = `${redirectURL}?orderId=${orderId}`;
            }
        }

        getBridgeSdk()
            .storeOrderInfoEvent(orderData, "dineout", orderInfoUrl)
            .catch(() => void 0);
    }
}

export const orderFailedEvent = ({ orderId, totalBill, failInstance, error }: IOrderFailedEvent): void => {
    let errorCode = DEFAULT_ERROR_CODE;
    let errorText = format(DEFAULT_ERROR_MESSAGE, failInstance);

    if (error && ("statusCode" in error || "statusMessage" in error)) {
        errorCode = String(error.statusCode ?? DEFAULT_ERROR_CODE);
        errorText = `${errorText} - ${error.statusMessage}`;
    } else if (error instanceof Error && error.message != null) {
        errorText = `${errorText} - ${error.message}`;
    }

    getBridgeSdk()
        .orderFailedEvent({
            orderId: orderId ? String(orderId) : "",
            totalBill,
            errorCode,
            errorText,
            marketplaceBusinessLine: MARKETPLACE_HEADERS.BUSINESS_LINE,
            marketplaceCategory: MARKETPLACE_HEADERS.MARKETPLACE_CATEGORY,
            paymentMethodData: {
                // Passing default value for paymentMethodData so that app doesn't throw error
                paymentCode: "",
                groupName: "",
                name: "",
                displayName: "",
                enabled: true,
                couponApplicable: false,
                meta: {},
                splitPayEnabled: false,
            },
        })
        .catch(() => void 0);
};

export { isCurrentUserHost };
